import { DialogTitle, Grid, IconButton, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import { Theme, makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import CloseIcon from '@material-ui/icons/Close'
import { toUpper, upperFirst, isEmpty } from 'lodash'
import React, { ChangeEvent, useEffect, useState } from 'react'

import {
  Policy_KindEnum,
  PolicyRule_VehicleTypesEnum,
  Provider,
  AreaLabelResponse,
  Policy_FeeCalculationMethodEnum,
  Policy_DueDateKindEnum,
  Policy_RecurrenceKindEnum,
} from 'src/api/gen'
import { CurrencyKind } from 'src/api/types/units'
import { getEnumEntries } from 'src/commons/filters/utils'
import { getTranslation } from 'src/commons/i18n'
import { enumsTranslationKeys, eventTypeTranslationKey } from 'src/commons/i18nEnums'
import { areDictEquals } from 'src/commons/policies/utils'
import { Validation } from 'src/commons/policies/validation'
import { ITemporalRange } from 'src/commons/temporalHelpers'
import { DateRangePicker } from 'src/components/DatePickers'
import { ConfirmationDialog, useDialog } from 'src/components/Dialog'
import NumericInput from 'src/components/Input/NumericInput'
import { MultiSelectChips, SelectBox } from 'src/components/Select'
import { useAgencySettings } from 'src/layout/AgencySettings'
import { FieldTitle } from 'src/pages/Policies/commons'

interface IFieldProps {
  disabled?: boolean
  id?: string
  title: string
  validation: Validation
}

interface INameProps extends IFieldProps {
  handleChange: (value: string) => void
  value: string
}

export const NameField = (props: INameProps) => {
  const { title, value, handleChange, validation, disabled } = props
  return (
    <div id={props.id} style={{ display: 'flex', flexDirection: 'column' }} data-testid="policy-editor-name-field">
      <FieldTitle title={title} />
      <TextField
        disabled={disabled}
        variant="outlined"
        margin="dense"
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
        error={!validation.isFieldValid('name')}
        helperText={validation.getFieldErrorMsg('name')}
      />
    </div>
  )
}

interface IKindProps extends IFieldProps {
  data: { id: string; label: string }[]
  handleChange: (value: any) => void
  inputProps?: { 'data-testid': 'kind-field' }
  value: Policy_KindEnum | undefined
}

export const KindField = (props: IKindProps) => {
  const { title, value, handleChange, validation, disabled, data, inputProps } = props

  return (
    <div id={props.id} style={{ display: 'flex', flexDirection: 'column' }} data-testid="policy-editor-kind-field">
      <FieldTitle title={title} />
      <SelectBox
        inputProps={inputProps}
        disabled={disabled}
        data={data}
        onChange={handleChange}
        value={value}
        error={!validation.isFieldValid('kind')}
        helperText={validation.getFieldErrorMsg('kind')}
      />
    </div>
  )
}

interface IFeeCalculationMethodProps extends IFieldProps {
  handleChange: (value: any) => void
  value: Policy_FeeCalculationMethodEnum
}

export const FeeCalculationMethodField = (props: IFeeCalculationMethodProps) => {
  const { t } = getTranslation()
  const { title, value, handleChange, disabled } = props

  return (
    <div id={props.id} style={{ display: 'flex', flexDirection: 'column' }}>
      <FieldTitle title={title} />
      <SelectBox
        disabled={disabled}
        data={Object.entries(enumsTranslationKeys.FeeCalculationMethod).map(([id, label]) => ({
          id,
          label: t(label),
        }))}
        onChange={handleChange}
        value={value}
      />
    </div>
  )
}

interface IDueDateProps extends IFieldProps {
  handleChange: (value: any) => void
  policyKind: Policy_KindEnum
  value: Policy_DueDateKindEnum
}

export const DueDateField = function (props: IDueDateProps) {
  const { t } = getTranslation()
  const initialEntries = Object.entries(enumsTranslationKeys.FeeDueDate).map(([id, label]) => ({
    id,
    label: t(label),
  }))

  const entries =
    props.policyKind === Policy_KindEnum.permit
      ? initialEntries
      : initialEntries.filter((elem) => elem.id === Policy_DueDateKindEnum.last_day)

  return (
    <div id={props.id} style={{ display: 'flex', flexDirection: 'column', width: '150px' }}>
      <FieldTitle title={props.title} />
      <SelectBox disabled={props.disabled} data={entries} onChange={props.handleChange} value={props.value} />
    </div>
  )
}

interface IProviderProps extends IFieldProps {
  // List of IDs
  handleChange: (value: any) => void
  providers?: Provider[] | undefined
  value?: string[] | undefined
}

const buildItemsFromProviders = (providers?: Provider[] | undefined) =>
  (providers || []).map((provider) => ({
    id: provider.id,
    label: provider.name,
  }))

export const ProviderField = (props: IProviderProps) => {
  const { title, value, handleChange, validation, disabled, providers } = props

  return (
    <div id={props.id} style={{ display: 'flex', flexDirection: 'column' }} data-testid="policy-editor-provider-field">
      <FieldTitle title={title} />
      <div style={{ height: 8 }} />
      <MultiSelectChips
        disabled={disabled}
        data={buildItemsFromProviders(providers)}
        oneLineSelect={true}
        onMultiChange={handleChange}
        value={value || []}
        error={!validation.isFieldValid('providers')}
        helperText={validation.getFieldErrorMsg('providers')}
        withSelectAll={true}
      />
    </div>
  )
}

interface IPeriodProps extends IFieldProps {
  end_date?: Temporal.ZonedDateTime
  handleChange: (value: ITemporalRange) => void
  minDate?: Temporal.ZonedDateTime
  startDisabled?: boolean
  start_date?: Temporal.ZonedDateTime
}

export const PeriodField = (props: IPeriodProps) => {
  const { title, start_date, end_date, handleChange, validation, disabled, startDisabled } = props

  return (
    <Grid id={props.id} container={true} data-testid="policy-editor-period-field">
      <Grid item={true} xs={12}>
        <FieldTitle title={title} />
      </Grid>
      <Grid item={true} xs={12}>
        <DateRangePicker
          disabled={disabled}
          startDisabled={startDisabled}
          value={{
            from: start_date,
            to: end_date,
          }}
          minDate={props.minDate}
          onChange={handleChange}
          clearable={true}
          withTime={true}
          error={{
            from: !validation.isFieldValid('start_date'),
            to: !validation.isFieldValid('end_date'),
          }}
          helperText={<div> {validation.getFieldErrorMsg('start_date')} </div>}
        />
      </Grid>
    </Grid>
  )
}

interface IDescriptionProps extends IFieldProps {
  handleChange: (value: string) => void
  value: string | undefined
}

export const DescriptionField = (props: IDescriptionProps) => {
  const { title, value, handleChange, disabled } = props

  return (
    <div id={props.id} style={{ display: 'flex', flexDirection: 'column' }}>
      <FieldTitle title={title} />
      <TextField
        data-testid="policy-editor-description-field"
        disabled={disabled}
        variant="outlined"
        margin="dense"
        value={value}
        multiline={true}
        minRows={5}
        maxRows={5}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange(e.target.value)}
      />
    </div>
  )
}

interface IIntegerProps extends IFieldProps {
  handleChange: (value: number) => void
  value: number
}

export const IntegerField = (props: IIntegerProps) => {
  const { title, value, handleChange, disabled } = props

  return (
    <div id={props.id} style={{ display: 'flex', flexDirection: 'column' }}>
      <FieldTitle title={title} />
      <NumericInput disabled={disabled} value={value} onChange={handleChange} />
    </div>
  )
}

interface ICurrencyProps extends IIntegerProps {
  currency?: string
  kind?: CurrencyKind
}

export const CurrencyIntegerField = (props: ICurrencyProps) => {
  const { title, value, handleChange, disabled, kind } = props
  const agencySettings = useAgencySettings()
  let currency: string

  if (props.currency) {
    currency = props.currency
  } else {
    if (kind === CurrencyKind.Permit) {
      currency = agencySettings.permit_currency_symbol
    } else {
      currency = agencySettings.penalty_symbol
    }
  }

  return (
    <div id={props.id} style={{ display: 'flex', flexDirection: 'column' }}>
      <FieldTitle title={title} />
      <NumericInput disabled={disabled} currency={currency} value={value} onChange={handleChange} />
    </div>
  )
}

interface IRuleFieldProps {
  disabled?: boolean
  handleChange: (value: any) => void
  id?: string
}

interface INumberField extends IRuleFieldProps {
  error?: boolean
  helperText?: string | null | undefined
  inputType?: 'outlined' | 'text'
  value: number
}

export const NumberField = (props: INumberField) => {
  return (
    <NumericInput
      id={props.id}
      value={props.value}
      onChange={props.handleChange}
      inputType={props.inputType}
      disabled={props.disabled}
    />
  )
}

interface IRuleNameProps extends IRuleFieldProps {
  error?: boolean
  helperText?: string | null | undefined
  value: string
}

export const RuleNameField = (props: IRuleNameProps) => {
  const { value, handleChange, disabled, error, helperText } = props
  /* Buffer trick taken from NumericInput
   * Piles the characters and them at once when the user moves to another
   * input/component (onBlur).
   */
  const [bufferValue, setBufferValue] = useState((value || 0).toString())
  useEffect(() => {
    setBufferValue(value || '')
  }, [value])

  const setValue = () => {
    handleChange(bufferValue)
    setBufferValue(bufferValue)
  }
  // Pretty neat, eh?

  return (
    <div id={props.id} style={{ display: 'flex', flexDirection: 'column' }}>
      <TextField
        variant="outlined"
        margin="dense"
        fullWidth={true}
        value={bufferValue}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setBufferValue(e.target.value)}
        onBlur={setValue}
        disabled={disabled}
        error={error}
        helperText={helperText}
      />
    </div>
  )
}

interface IGeographiesProps extends IRuleFieldProps {
  allGeographies: AreaLabelResponse[]
  error?: boolean
  helperText: React.ReactNode
  value: string[]
}

export const GeographiesField = (props: IGeographiesProps) => {
  const { value, handleChange, allGeographies, disabled, error, helperText } = props

  return (
    <MultiSelectChips
      id={props.id}
      disabled={disabled}
      oneLineSelect={true}
      data={allGeographies}
      onMultiChange={handleChange}
      value={value}
      error={error}
      helperText={helperText}
      withSelectAll={true}
    />
  )
}

interface IVehicleTypesProps {
  disabled?: boolean
  error?: boolean
  handleChange: (value: any) => void
  helperText: React.ReactNode
  id?: string
  value: PolicyRule_VehicleTypesEnum[]
}

export const VehicleTypesField = (props: IVehicleTypesProps) => {
  const { vehicle_types } = useAgencySettings()
  const { value, handleChange, disabled, error, helperText } = props

  return (
    <MultiSelectChips
      id={props.id}
      disabled={disabled}
      oneLineSelect={true}
      data={getEnumEntries(vehicle_types, enumsTranslationKeys.DeviceType, undefined, false)}
      onMultiChange={handleChange}
      value={value}
      error={error}
      helperText={helperText}
      withSelectAll={true}
    />
  )
}

interface IVehicleCategoriesProps {
  disabled?: boolean
  error?: boolean
  handleChange: (value: any) => void
  helperText: React.ReactNode
  id?: string
  value: string[]
}

// This component was made to simplify the creation of Beginner policy rules by directly offer the choice
// of vehicle categories with the mapping set in the back.
// However I'm still not sure about its relevancy because we still use "vehicle_types" everywhere else
// and the code is also not very clear.
export function VehicleCategoriesField(props: IVehicleCategoriesProps) {
  const { vehicle_types, device_type_to_category_mapping } = useAgencySettings()

  // Kinda looks like a trick to get the categories for a specific device type
  const vehicleCategories = new Set(
    vehicle_types.map((vehicle_type) => device_type_to_category_mapping[vehicle_type as any])
  )
  const displayedValue = props.value.map((subValue) => device_type_to_category_mapping[subValue as any])

  return (
    <MultiSelectChips
      id={props.id}
      disabled={props.disabled}
      oneLineSelect={true}
      data={getEnumEntries([...vehicleCategories], enumsTranslationKeys.DeviceCategory, undefined, false)}
      onMultiChange={props.handleChange}
      value={displayedValue}
      error={props.error}
      helperText={props.helperText}
      withSelectAll={true}
    />
  )
}

interface IDialogFieldProps {
  closeButtonTitle: string
  dialog: ReturnType<typeof useDialog>
  dialogActions?: (handleClose: () => void) => JSX.Element
  dialogContent: JSX.Element
  disabled?: boolean
  openButtonTitle: string
  title?: string
  tooltip?: string
}

export const DialogField = function (props: IDialogFieldProps) {
  const { isOpen, open, close, handleClose } = props.dialog

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {props.title && <FieldTitle title={props.title} tooltip={props.tooltip} />}
        <Button variant="outlined" onClick={open} style={{ width: 'fit-content', marginTop: '8px' }} size="large">
          {props.openButtonTitle}
        </Button>
      </div>
      <Dialog open={isOpen} maxWidth={false}>
        <DialogContent>{props.dialogContent}</DialogContent>
        <DialogActions>
          {props.disabled ? (
            <Button variant="contained" color="primary" onClick={close}>
              {props.closeButtonTitle}
            </Button>
          ) : !props.dialogActions ? (
            <Button variant="contained" color="primary" onClick={handleClose}>
              {props.closeButtonTitle}
            </Button>
          ) : (
            props.dialogActions(handleClose)
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

interface IConfirmationDialogFieldProps extends Omit<IDialogFieldProps, 'dialogActions' | 'dialog'> {
  confirmationText: string
  dialogActions?: (close: () => void, handleClose: () => void, openConfirm: () => void) => JSX.Element
  onClose?: () => void
  validate?: () => boolean
}

export const ConfirmationDialogField = function (props: IConfirmationDialogFieldProps) {
  const dialog = useDialog({
    onClose: props.onClose,
  })
  const {
    isOpen: confirmIsOpen,
    open: openConfirm,
    close: closeConfirm,
    handleClose: handleConfirmClose,
  } = useDialog({
    onClose: dialog.handleClose,
  })

  return (
    <>
      <DialogField
        openButtonTitle={props.openButtonTitle}
        title={props.title}
        closeButtonTitle={props.closeButtonTitle}
        dialogContent={props.dialogContent}
        dialog={dialog}
        dialogActions={
          props.dialogActions && ((handleClose) => props.dialogActions!(dialog.close, handleClose, openConfirm))
        }
        disabled={props.disabled}
      />
      <ConfirmationDialog
        open={confirmIsOpen}
        action="confirm"
        onClose={closeConfirm}
        onConfirmation={handleConfirmClose}
        descriptionMessage={props.confirmationText}
      />
    </>
  )
}

interface IVehicleEventStatesSelectorProps {
  disabled: boolean
  handleChange: (vehicle_events_and_states: any) => void
  isPublished: boolean
  policy_kind: Policy_KindEnum
  policy_vehicle_events_and_states?: { [key: string]: Array<string> }
}

export const useVehicleEventStatesSelector = (
  policy_kind: Policy_KindEnum,
  isPublished: boolean,
  policy_vehicle_events_and_states?: { [key: string]: Array<string> }
) => {
  const agencySettings = useAgencySettings()
  const vehicle_state_events = agencySettings.vehicle_state_events?.[policy_kind] ?? {}

  const [events, setEvents] = useState<{ [key: string]: Array<string> }>(policy_vehicle_events_and_states ?? {})
  useEffect(() => {
    if (!isPublished) {
      selectAll()
    }
  }, [policy_kind])

  const updateEvents = (state: string, event: string) => {
    const newEvents = { ...events }
    if (!(state in newEvents)) {
      newEvents[state] = []
    }

    const eventIndex = newEvents[state].indexOf(event)
    if (eventIndex === -1) {
      newEvents[state].push(event)
    } else {
      newEvents[state].splice(eventIndex, 1)
    }
    setEvents(newEvents)
  }

  const selectAll = () => {
    const newEvents = {} as { [key: string]: Array<string> }
    Object.entries(vehicle_state_events).forEach(([state, event_types]) => {
      newEvents[state] = event_types.map((event) => event)
    })
    setEvents(newEvents)
  }

  const deselectAll = () => {
    setEvents({})
  }

  const empty = isEmpty(vehicle_state_events)

  return { empty, vehicle_state_events, events, updateEvents, selectAll, deselectAll }
}

export const VehicleEventStatesSelector = (props: IVehicleEventStatesSelectorProps) => {
  const { t } = getTranslation()
  const { empty, vehicle_state_events, events, updateEvents, selectAll, deselectAll } = useVehicleEventStatesSelector(
    props.policy_kind,
    props.isPublished,
    props.policy_vehicle_events_and_states
  )

  if (empty) {
    return null
  }

  return (
    <ConfirmationDialogField
      title={upperFirst(t('titles.configure-events'))}
      tooltip={t('tooltips.configure-events')}
      openButtonTitle={t('buttons.configure-events.open')}
      closeButtonTitle={t('buttons.configure-events.close')}
      dialogContent={
        <>
          {Object.entries(vehicle_state_events).map(([state, event_types]) => (
            <FormControl component="fieldset" variant="standard" key={state}>
              <FormLabel>
                <Typography variant="h6" color="textPrimary">
                  {toUpper(t(enumsTranslationKeys.VehicleStates[state]))}
                </Typography>
              </FormLabel>
              <FormGroup key={state}>
                {event_types.map((event) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={events[state]?.includes(event) ?? false}
                        onChange={() => updateEvents(state, event)}
                        color="primary"
                        disabled={props.disabled}
                      ></Checkbox>
                    }
                    label={upperFirst(t(eventTypeTranslationKey(event)))}
                    key={event}
                  />
                ))}
              </FormGroup>
            </FormControl>
          ))}
        </>
      }
      confirmationText={t('descriptions.configure-events.confirm')}
      dialogActions={(_, handleClose, openConfirm) => (
        <>
          <Button variant="contained" color="primary" onClick={selectAll}>
            {t<string>('buttons.configure-events.select-all')}
          </Button>
          <Button variant="contained" color="primary" onClick={deselectAll}>
            {t<string>('buttons.configure-events.deselect-all')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (!areDictEquals(events, vehicle_state_events)) {
                openConfirm()
              } else {
                handleClose()
              }
            }}
            disabled={isEmpty(events)}
          >
            {t<string>('buttons.configure-events.save')}
          </Button>
        </>
      )}
      onClose={() => props.handleChange(events)}
    />
  )
}

function useRecurrenceStyles() {
  return makeStyles((theme: Theme) => ({
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    paper: { minWidth: '600px' },
  }))()
}

interface IRepeatEveryField {
  disabled: boolean
  handleRecurrenceCountChange: (recurrence: number) => void
  handleRecurrenceKindChange: (recurrenceKind: any) => void
  recurrenceCount?: number
  recurrenceKind?: Policy_RecurrenceKindEnum
}

const RepeatEveryField = function (props: IRepeatEveryField) {
  const { t } = getTranslation()
  const recurrenceKindEntries = Object.entries(enumsTranslationKeys.RecurrenceKind).map(([id, label]) => ({
    id,
    label: t(label),
  }))

  return (
    <Grid container spacing={3}>
      <Grid item xs={4}>
        <Typography variant={'subtitle2'} style={{ padding: '10px 0' }}>
          {t<string>('Repeat every')}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <NumericInput
          disabled={props.disabled}
          value={props.recurrenceCount}
          onChange={props.handleRecurrenceCountChange}
        />
      </Grid>
      <Grid item xs={4}>
        <div style={{ marginTop: '-8px' }}>
          <SelectBox
            disabled={props.disabled}
            data={recurrenceKindEntries}
            onChange={props.handleRecurrenceKindChange}
            value={props.recurrenceKind}
          />
        </div>
      </Grid>
    </Grid>
  )
}

interface IEndsAfterProps {
  disabled: boolean
  handleChange: (value: number) => void
  value?: number
}

const EndsAfterField = function (props: IEndsAfterProps) {
  const { t } = getTranslation()
  return (
    <Grid container spacing={3}>
      <Grid item xs={4}>
        <Typography variant={'subtitle2'} style={{ padding: '10px 0' }}>
          {t<string>('Ends after')}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <NumericInput disabled={props.disabled} value={props.value} onChange={props.handleChange} />
      </Grid>
    </Grid>
  )
}

interface IRecurrenceDialogTitleProps {
  children: React.ReactNode
  onClose: () => void
}

const RecurrenceDialogTitle = function (props: IRecurrenceDialogTitleProps) {
  const classes = useRecurrenceStyles()
  return (
    <DialogTitle disableTypography>
      <Typography variant="h5">{props.children}</Typography>
      {props.onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={props.onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  )
}

interface IRecurrenceFieldProps {
  disabled: boolean
  handleRecurrenceChange: (
    recurrenceCount: number | undefined,
    recurrenceKind: Policy_RecurrenceKindEnum | undefined,
    recurrenceEndsAfter: number | undefined
  ) => void
  recurrenceEndsAfter?: number
  recurrenceKind?: Policy_RecurrenceKindEnum
  recurrenceRepeatEvery?: number
  title: string
}
export const RecurrenceField = function (props: IRecurrenceFieldProps) {
  const { t } = getTranslation()
  const classes = useRecurrenceStyles()

  const [open, setOpen] = useState<boolean>(false)
  const [recurrenceRepeatEvery, setRecurrenceRepeatEvery] = useState<number | undefined>(undefined)
  const [recurrenceEndsAfter, setRecurrenceEndsAfter] = useState<number | undefined>(undefined)
  const [recurrenceKind, setRecurrenceKind] = useState<Policy_RecurrenceKindEnum | undefined>(undefined)

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setRecurrenceRepeatEvery(props.recurrenceRepeatEvery)
    setRecurrenceEndsAfter(props.recurrenceEndsAfter)
    setRecurrenceKind(props.recurrenceKind)
    setOpen(false)
  }

  const handleClear = () => {
    setRecurrenceRepeatEvery(0)
    setRecurrenceEndsAfter(0)
    setRecurrenceKind(undefined)
  }

  const handleSave = () => {
    props.handleRecurrenceChange(recurrenceRepeatEvery, recurrenceKind, recurrenceEndsAfter)
    setOpen(false)
  }

  useEffect(() => {
    setRecurrenceRepeatEvery(props.recurrenceRepeatEvery)
    setRecurrenceEndsAfter(props.recurrenceEndsAfter)
    setRecurrenceKind(props.recurrenceKind)
  }, [props.recurrenceRepeatEvery, props.recurrenceKind, props.recurrenceEndsAfter])

  return (
    <div>
      <Button variant="outlined" onClick={handleClickOpen}>
        {props.title}
      </Button>
      <Dialog open={open} onClose={handleClose} classes={{ paper: classes.paper }}>
        <div style={{ padding: '20px' }}>
          <RecurrenceDialogTitle onClose={handleClose}>{props.title}</RecurrenceDialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <RepeatEveryField
                recurrenceCount={recurrenceRepeatEvery}
                recurrenceKind={recurrenceKind}
                handleRecurrenceCountChange={(value) => setRecurrenceRepeatEvery(value)}
                handleRecurrenceKindChange={(value) => setRecurrenceKind(value)}
                disabled={props.disabled}
              />
              <EndsAfterField
                value={recurrenceEndsAfter}
                handleChange={(value) => setRecurrenceEndsAfter(value)}
                disabled={props.disabled}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button disabled={props.disabled} onClick={handleClear}>
              {t<string>('recurrence.reset')}
            </Button>
            <Button disabled={props.disabled} onClick={handleSave}>
              {t<string>('recurrence.save')}
            </Button>
          </DialogActions>
        </div>
      </Dialog>
    </div>
  )
}
